import { useEffect, useMemo, useState } from 'react'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import {
  BreadcrumbObjectType,
  useGetTablesSimpleQuery,
} from '../../common/generated/apollo-operations'
import { useOrgProjects } from '../../common/hooks/useOrgProjects'
import { DossIcon } from '../DossIcon'
import { WorkspaceIcon } from '../navigation/WorkspaceIcon'
import { SimpleTable } from '../tables/types'
import { LoadingSpinner } from '../ui/atoms/loading-spinner/LoadingSpinner'

export type TablePickerProps = {
  requiresFullViewAccess: boolean
  defaultTableId?: string
  defaultWorkspaceId?: string
  onChange?: (table: SimpleTable | null) => void
  placeholder?: string
  selectedTable?: SimpleTable | null
  disabled?: boolean
  label?: string
}

/**
 * Dropdown component for selecting a table (includes both parent and child tables)
 *
 * If requiresFullViewAccess is true, only the tables that the user has full view access to are returned;
 * else all tables that the user have any level of access to are returned (controlled by RLS)
 */
export function TablePicker({
  defaultTableId,
  defaultWorkspaceId,
  onChange,
  placeholder,
  selectedTable,
  disabled,
  requiresFullViewAccess,
  label,
}: TablePickerProps) {
  const { selectedProject } = useOrgProjects()
  const { data: tablesData, loading: tablesLoading } = useGetTablesSimpleQuery({
    variables: { input: { fullViewAccess: requiresFullViewAccess } },
  })
  const tables = useMemo(() => {
    return tablesData?.tables || []
  }, [tablesData?.tables])
  const [targetWorkspaceId, setTargetWorkspaceId] = useState<string | null>(
    defaultWorkspaceId || selectedTable?.rootWorkspace.id || null,
  )
  const value = tables.find((t) => t.rootWorkspace.id === targetWorkspaceId) || null

  const icon = selectedTable ? (
    <WorkspaceIcon
      icon={selectedTable.icon ? <DossIcon name={selectedTable.icon.name} size={12} /> : undefined}
      size={12}
      workspaceType={BreadcrumbObjectType.TABLE}
    />
  ) : (
    <WorkspaceIcon size={12} workspaceType={BreadcrumbObjectType.PROJECT} />
  )

  const adornment = tablesLoading ? <LoadingSpinner type="table-cell" /> : icon

  useEffect(() => {
    if (selectedTable) {
      setTargetWorkspaceId(selectedTable.rootWorkspace.id)
    }
  }, [selectedTable])

  useEffect(() => {
    if (defaultTableId) {
      const defaultTable = tables.find((t) => t.id === defaultTableId)
      if (defaultTable) {
        setTargetWorkspaceId(defaultTable.rootWorkspace.id)
        onChange?.(defaultTable)
      }
    }
    // onChange cannot be in the dependency array
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, defaultTableId])

  const tableOptions = useMemo(
    () =>
      tables.map((table) => {
        let label = table.name
        if (table.parentTable) {
          label = `${table.parentTable.name} / ${table.name}`
        }
        const startIcon = (
          <WorkspaceIcon
            icon={table.icon ? <DossIcon name={table.icon.name} size={12} /> : undefined}
            size={12}
            workspaceType={BreadcrumbObjectType.TABLE}
          />
        )
        return {
          value: table.rootWorkspace.id,
          label,
          startIcon,
        }
      }),
    [tables],
  )

  return (
    <div className="relative flex flex-col gap-0">
      <div className="flex flex-col gap-2 text-gray-9">
        <Dropdown
          disabled={disabled || tablesLoading}
          label={label}
          onChange={(newValue) => {
            setTargetWorkspaceId(newValue || null)
            onChange?.(
              newValue ? tables.find((table) => table.rootWorkspace.id === newValue) || null : null,
            )
          }}
          options={tableOptions}
          placeholder={placeholder || selectedProject?.name}
          startAdornment={adornment}
          value={value?.rootWorkspace.id}
        />
      </div>
    </div>
  )
}
